/*
 * BLT Cart controller — one thin delegated controller for the drawer AND the cart page.
 *
 * Design principle: every mutation re-renders the affected cart sections through Shopify's
 * Section Rendering API (the `sections` param on /cart/change.js, /cart/update.js, /cart/add.js).
 * All money / reward math lives in Liquid, so this file only fetches, swaps the rendered HTML
 * into each live [data-blt-cart-root], keeps the header count in sync, and drives the drawer.
 *
 * Roots: any element with [data-blt-cart-root] carrying:
 *   data-section       — the section id to request from Section Rendering
 *   data-context       — 'drawer' | 'page'
 *   data-cart-count    — cart.item_count at render time
 *   data-applied-codes — comma-joined applicable discount codes at render time
 *
 * No ES module imports — loaded once globally via <script defer>.
 */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-blt-cart]'); // the global drawer shell
  var pending = false;

  /* -------- helpers -------- */
  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-blt-cart-root]'));
  }
  function sectionParam() {
    var ids = roots()
      .map(function (r) { return r.getAttribute('data-section'); })
      .filter(function (id, i, a) { return id && a.indexOf(id) === i; });
    return ids.join(',');
  }
  function appliedCodes() {
    // authoritative list of currently-applicable codes (from any root, they match)
    var r = roots()[0];
    var raw = (r && r.getAttribute('data-applied-codes')) || '';
    return raw.split(',').map(function (c) { return c.trim(); }).filter(Boolean);
  }
  function unique(arr) {
    var seen = {}, out = [];
    arr.forEach(function (c) {
      var k = c.toLowerCase();
      if (!seen[k]) { seen[k] = 1; out.push(c); }
    });
    return out;
  }
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  /* -------- render swapping -------- */
  function swapSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var doc = new DOMParser().parseFromString(sections[id], 'text/html');
      var fresh =
        doc.querySelector('[data-blt-cart-root][data-section="' + id + '"]') ||
        doc.querySelector('[data-blt-cart-root]');
      if (!fresh) return;
      document
        .querySelectorAll('[data-blt-cart-root][data-section="' + id + '"]')
        .forEach(function (target) {
          target.innerHTML = fresh.innerHTML;
          if (fresh.hasAttribute('data-cart-count')) {
            target.setAttribute('data-cart-count', fresh.getAttribute('data-cart-count'));
          }
          if (fresh.hasAttribute('data-applied-codes')) {
            target.setAttribute('data-applied-codes', fresh.getAttribute('data-applied-codes'));
          }
        });
    });
    syncHeader();
  }

  function syncHeader() {
    var r = roots()[0];
    if (!r) return;
    var n = parseInt(r.getAttribute('data-cart-count') || '0', 10) || 0;
    document.querySelectorAll('.blt-header__cart').forEach(function (a) {
      a.textContent = n > 0 ? 'cart (' + n + ')' : 'cart';
    });
    document.querySelectorAll('[data-cart-count-badge]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n < 1;
    });
  }

  function dispatchCartUpdate() {
    document.dispatchEvent(
      new CustomEvent('cart:update', { bubbles: true, detail: { data: { source: 'blt-cart' } } })
    );
  }

  /* -------- drawer open / close -------- */
  var lastFocused = null;
  function focusables() {
    var panel = drawer && drawer.querySelector('.blt-cart__panel');
    if (!panel) return [];
    return Array.prototype.slice
      .call(panel.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])'))
      .filter(function (el) { return el.offsetParent !== null; });
  }
  function openDrawer() {
    if (!drawer || drawer.classList.contains('is-open')) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('blt-cart-locked');
    if (window.BltScrollLock) BltScrollLock.lock(); // iOS-safe background lock
    var close = drawer.querySelector('.blt-cart__back');
    if (close) requestAnimationFrame(function () { close.focus(); });
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('blt-cart-locked');
    if (window.BltScrollLock) BltScrollLock.unlock();
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }
  function refreshDrawer(openAfter) {
    var root = drawer && drawer.querySelector('[data-blt-cart-root]');
    if (!root) { if (openAfter) openDrawer(); return; }
    var id = root.getAttribute('data-section');
    var base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(base + '?section_id=' + id)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var obj = {}; obj[id] = html;
        swapSections(obj);
        if (openAfter) openDrawer();
      })
      .catch(function () { if (openAfter) openDrawer(); });
  }

  /* -------- mutations -------- */
  function setBusy(el, on) { if (el) el.classList.toggle('is-busy', !!on); }

  function change(key, qty, li) {
    if (pending) return;
    pending = true;
    setBusy(li, true);
    postJSON('/cart/change.js', {
      id: key,
      quantity: qty,
      sections: sectionParam(),
      sections_url: window.location.pathname
    })
      .then(function (cart) {
        if (cart && cart.status) return; // error response (e.g. 422) — leave the row as-is
        swapSections(cart.sections);
        dispatchCartUpdate();
      })
      .catch(function () {})
      .then(function () { pending = false; setBusy(li, false); });
  }

  function addKit(variantId, btn, ctx) {
    if (pending || !variantId) return;
    pending = true;
    setBusy(btn, true);
    postJSON('/cart/add.js', {
      items: [{ id: parseInt(variantId, 10), quantity: 1 }],
      sections: sectionParam(),
      sections_url: window.location.pathname
    })
      .then(function (res) {
        if (res && res.status) return; // add error (e.g. sold out) — leave UI as-is
        swapSections(res.sections);
        if (ctx !== 'page') openDrawer(); // on the cart page, don't pop the drawer over the page
        dispatchCartUpdate();
      })
      .catch(function () {})
      .then(function () { pending = false; setBusy(btn, false); });
  }

  function submitCodes(codes, form, newCode) {
    if (pending) return;
    pending = true;
    if (form) form.classList.add('is-busy');
    postJSON('/cart/update.js', {
      discount: codes.join(','),
      sections: sectionParam(),
      sections_url: window.location.pathname
    })
      .then(function (cart) {
        swapSections(cart.sections);
        dispatchCartUpdate();
        if (newCode) {
          var dc = (cart.discount_codes || []).filter(function (x) {
            return x.code && x.code.toLowerCase() === newCode.toLowerCase();
          })[0];
          if (dc && dc.applicable === false) showCouponError("that code isn’t valid");
          else clearCouponError();
        }
      })
      .catch(function () {})
      .then(function () {
        pending = false;
        if (form) form.classList.remove('is-busy');
      });
  }

  function applyCode(code, form) {
    submitCodes(unique(appliedCodes().concat([code])), form, code);
    var input = form && form.querySelector('[data-coupon-input]');
    if (input) input.value = '';
  }
  function removeCode(code) {
    var next = appliedCodes().filter(function (c) { return c.toLowerCase() !== code.toLowerCase(); });
    submitCodes(next, null, null);
  }
  function showCouponError(msg) {
    document.querySelectorAll('[data-coupon-error]').forEach(function (el) {
      el.textContent = msg; el.hidden = false;
    });
  }
  function clearCouponError() {
    document.querySelectorAll('[data-coupon-error]').forEach(function (el) {
      el.textContent = ''; el.hidden = true;
    });
  }

  /* -------- delegated events -------- */
  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-blt-cart-open]');
    if (openBtn) { e.preventDefault(); openDrawer(); refreshDrawer(false); return; }
    if (e.target.closest('[data-blt-cart-close]')) { closeDrawer(); return; }

    var scope = e.target.closest('[data-blt-cart-root]');
    if (!scope) return;

    var removeCodeBtn = e.target.closest('[data-remove-code]');
    if (removeCodeBtn) { e.preventDefault(); removeCode(removeCodeBtn.getAttribute('data-remove-code')); return; }

    var addBtn = e.target.closest('[data-kit-add]');
    if (addBtn && !addBtn.disabled) { e.preventDefault(); addKit(addBtn.getAttribute('data-kit-variant'), addBtn, scope.getAttribute('data-context')); return; }

    var li = e.target.closest('[data-line-key]');
    if (!li) return;
    var key = li.getAttribute('data-line-key');
    var qtyEl = li.querySelector('[data-qty]');
    var qty = parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1;
    if (e.target.closest('[data-qty-up]')) change(key, qty + 1, li);
    else if (e.target.closest('[data-qty-down]')) change(key, Math.max(0, qty - 1), li);
    else if (e.target.closest('[data-remove]')) change(key, 0, li);
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-coupon-form]');
    if (!form) return;
    e.preventDefault();
    var input = form.querySelector('[data-coupon-input]');
    var code = ((input && input.value) || '').trim();
    if (code) applyCode(code, form);
  });

  document.addEventListener('keydown', function (e) {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key === 'Tab') {
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  // Auto-open + refresh when the cart changes elsewhere (e.g. PDP add-to-cart dispatches cart:update).
  document.addEventListener('cart:update', function (ev) {
    var src = ev && ev.detail && ev.detail.data && ev.detail.data.source;
    if (src === 'blt-cart') return; // our own change — already swapped
    openDrawer();
    refreshDrawer(false);
  });

  syncHeader();
})();
